// src/CcpAdminUsers/Lists/Lists.js
import React, { Fragment, useEffect, useState } from "react";
import GridToolbarNoExport from "../../../common/GridToolbarNoExport";
import AppDataGrid from "../../../common/AppDataGrid";
import {
  IconButton,
  Snackbar,
  Dialog,
  Paper,
  Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db, deleteUser } from "../../../../config/firebase";
import EditModal from "./EditModal";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";

const Lists = ({ listRefresh }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchUsers = async () => {
    const q = query(
      collection(db, "users"),
      where("ccpDistrictFlag", "==", true),
      orderBy("district"),
    );
    const querySnapshot = await getDocs(q);
    const usersList = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setUsers(usersList);
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, [listRefresh]);

  const handleEditOpen = (user) => {
    setSelectedUser(user);
    setOpenEdit(true);
  };

  const handleEditClose = () => {
    setOpenEdit(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleEditSuccess = () => {
    setSnackbarMessage("User has been updated");
    setSnackbarOpen(true);
    handleEditClose();
    // Fetch updated users
    fetchUsers();
  };

  const handleAddSuccess = () => {
    setSnackbarMessage("New user has been created successfully");
    setSnackbarOpen(true);
    // Fetch updated users
    fetchUsers();
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true); // Set deleting to true
    try {
      // Perform deletion from Firebase Auth and Firestore
      const result = await deleteUser(selectedUser.id); // Assuming id is the user's document ID

      if (result.success) {
        setSnackbarSeverity("success");
        setSnackbarMessage("User deleted successfully");
      } else {
        setSnackbarSeverity("error");
        setSnackbarMessage("Failed to delete user");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      setSnackbarSeverity("error");
      setSnackbarMessage("Failed to delete user");
    } finally {
      setSnackbarOpen(true);
      setOpenDeleteDialog(false);
      setDeleting(false); // Set deleting to false
      // Fetch updated users
      fetchUsers();
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
  };

  const columns = [
    { field: "name", headerName: "Name", flex: 1 },
    { field: "district", headerName: "District", flex: 1 },
    { field: "email", headerName: "Email", flex: 1 },
    { field: "phone", headerName: "Phone", flex: 1 },

    { field: "id", headerName: "id", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      renderCell: (params) => (
        <Fragment>
          <IconButton onClick={() => handleEditOpen(params.row)}>
            <EditIcon className="edit-icon" />
          </IconButton>
          <IconButton onClick={() => handleDeleteClick(params.row)}>
            <DeleteIcon style={{ color: "red" }} />
          </IconButton>
        </Fragment>
      ),
    },
  ];

  return (
    <Fragment>
      <Paper mt={2} mb={2} ml={2} mr={2}>
        <AppDataGrid
          sx={{ p: 3 }}
          initialState={{
            columns: {
              columnVisibilityModel: {
                // Hide columns status and traderName, the other columns will remain visible
                id: false,
              },
            },
          }}
          rows={users}
          columns={columns}
          loading={loading}
          slots={{ toolbar: GridToolbarNoExport }}
          autosizeOnMount
          autoHeight
          pagination
          pageSize={25}
          pageSizeOptions={[25]}
          headerClassName="custom-header-class"
        />
      </Paper>
      <Dialog open={openEdit} onClose={handleEditClose}>
        <EditModal
          user={selectedUser}
          onSuccess={handleEditSuccess}
          onClose={handleEditClose}
        />
      </Dialog>

      <ConfirmDeleteDialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        deleting={deleting}
        title="Confirm Delete"
        message="Are you sure you want to delete this user? This action cannot be undone."
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Fragment>
  );
};

export default Lists;
